import { threatColor } from '../render/threatColor';
import {
  hourName,
  getArt,
  getSchool,
  monsterRank,
  rankFor,
  rankName,
  SKILL_KIND_LABELS,
  type LogChannel,
  type PlayerCommand,
  type SkillDef,
  type SkillKind,
  type World,
  type WorldEvent,
  itemOf,
  getMount,
  getNeigong,
  getTitle,
  KARMA_LABELS,
  STATUSES,
  type StatusState,
  LADDERS,
  PARTY,
  type Entity,
  getQinggong,
  fmtMult,
  questGiverLine,
  ch2Goal,
  originFullOf,
  originOf,
  showsPlainsQuest,
} from '@nine/shared';
import { ctx2d } from '../render/pixel';
import { actionIcon, currencyIcon, iconEl, skillIcon, statusIcon } from '../render/icons';
import { STATUS_EXTRAS } from '../icons/status/kind';
import { drawPortrait, type SpriteBank } from '../render/sprites';
import { $, fmtClock, h, setText, setWidth } from './dom';
import { dashTip, hotbarKey, jumpTip, sprintTip, type ActionTip } from './qinggong';
import { drawSpotMarks, SpotBadge, spotMessage } from './trainingSpot';
import { drawLifePings, lifeTitle } from './lifePings';
import { drawQuestPing } from './questPing';
import { groundDrag } from './groundAimTouch';
import { syncUltReady } from './ultReady';

export interface HudActions {
  command(cmd: PlayerCommand): void;
  setBlock(on: boolean): void;
  dash(): void;
  /** Uses the move in a hotbar slot (aiming first if it is ground-targeted). */
  useSlot(slot: number): void;
  /** Opens a tab of the character menu. */
  openPanel(tab: 'arts' | 'shop'): void;
}

type ChatChannel = LogChannel | 'world' | 'guild';
const CHANNELS: Record<ChatChannel, [string, string]> = {
  world: ['c-world', 'โลก'],
  sys: ['c-sys', 'ระบบ'],
  guild: ['c-guild', 'สำนัก'],
  fight: ['c-fight', 'ต่อสู้'],
};

const BREAK = SKILL_KIND_LABELS.break;

const AMBIENT_CHAT: [ChatChannel, string][] = [
  ['world', 'ดาบเดียวพิชิตฟ้า: หาปาร์ตี้ลงถ้ำมังกรหลับ ขาดหมอหนึ่งคน'],
  ['world', `จันทร์เคียงลม: เห็นไอคอน 守 บนหัวโจร อย่าตีตรงๆ ใช้${BREAK}สิ`],
  ['guild', 'หมอกม่วงพันลี้: คืนนี้สองทุ่มรวมพลที่ภูเขาฮัวซาน'],
  ['world', 'ไร้เงาไร้รอย: ใครเห็นปีศาจกระบี่ทมิฬบ้าง ได้ยินว่าดรอปกระบี่เล่มที่สิบ'],
  ['sys', 'ประกาศ: ศึกประลองยุทธ์ข้ามเซิร์ฟเวอร์จะเริ่มในอีก 30 นาที'],
  ['world', 'ศิษย์น้องเสี่ยวหลง: นั่งสมาธิ (V) ได้แต้มฝึกปรือเร็วกว่ายืนเฉยตั้งห้าเท่า'],
  ['guild', 'ดาบเดียวพิชิตฟ้า: เจ้าสำนักออนไลน์แล้ว'],
  ['world', 'พิณเย็นริมธาร: รับสอนเพลงกระบี่มือใหม่ฟรี'],
];

const KIND_CLASS: Record<SkillKind, string> = {
  attack: 'k-attack',
  break: 'k-break',
  heal: 'k-heal',
  power: 'k-power',
  buff: 'k-buff',
};
/** A kind of move with its seal glyph, in the words the skill data uses (SKILL_KIND_LABELS). */
export const KIND_LABEL: Record<SkillKind, string> = {
  attack: `攻 ${SKILL_KIND_LABELS.attack}`,
  break: `破 ${SKILL_KIND_LABELS.break}`,
  heal: `療 ${SKILL_KIND_LABELS.heal}`,
  power: `怒 ${SKILL_KIND_LABELS.power}`,
  buff: `氣 ${SKILL_KIND_LABELS.buff}`,
};

/** The frame a move gets on the hotbar (guard-break, ultimate, buff/heal, ground-targeted) and why. */
function moveMark(s: SkillDef): [string, string] | null {
  if (s.kind === 'power') return ['power', 'ใช้ความโกรธ'];
  if (s.kind === 'break')
    return ['break', 'คูลดาวน์ 1 วินาที · ใช้กลางอากาศเพื่อกระโดดได้อีกครั้ง'];
  if (s.ground) return ['ground', 'เลือกพื้นที่ด้วยเมาส์'];
  if (s.kind === 'buff' || s.kind === 'heal') return ['buff', ''];
  return null;
}

/**
 * A move's face on the hotbar: its 32 px icon at 1×, which carries the kind mark (the 破 seal, the
 * 怒 frame, the 療/氣 tab). The Edge-glass mockup's kind colours stay on the key's frame (orange
 * 破, green 療/氣, red 怒) and the move's Han glyph moves to the tooltip's title: in a 46 px key a
 * glyph corner beside the key number, the cost and the icon's own marks did not read at 1×.
 */
const moveFace = (s: SkillDef): HTMLCanvasElement => iconEl(skillIcon(s), 'face');

/**
 * A state a status row shows besides the game's statuses (STATUS_EXTRAS in icons/status/kind.ts):
 * stunned, meditating, standing in a training spot, the group dance's afterglow.
 */
interface RowState {
  readonly id: string;
  readonly stacks: number;
  /** Seconds left (Infinity: no countdown). */
  readonly t: number;
  /** The tooltip's words (the game's statuses use their own). */
  readonly desc?: string;
}

/** Small icons for an entity's statuses, with stack counts; rebuilt only when they change. */
function syncStatusRow(el: HTMLElement, list: readonly (StatusState | RowState)[]): void {
  const key = list.map((s) => `${s.id}${s.stacks}`).join();
  // Seconds left, or whole minutes (น.) once it is a minute or more (the dance's afterglow).
  const left = (s: { t: number }) =>
    !Number.isFinite(s.t) ? '' : s.t >= 60 ? `${Math.ceil(s.t / 60)}น.` : String(Math.ceil(s.t));
  if (el.dataset.key === key) {
    for (const s of list) {
      const t = el.querySelector<HTMLElement>(`[data-st="${s.id}"] .st-t`);
      if (t) t.textContent = left(s);
    }
    return;
  }
  el.dataset.key = key;
  el.replaceChildren(
    ...list.map((s) => {
      const d = STATUSES[s.id];
      const x = STATUS_EXTRAS[s.id];
      const kind = d?.kind ?? (x?.debuff ? 'debuff' : 'buff');
      const desc = d?.desc ?? ('desc' in s ? s.desc : '') ?? '';
      // Hover (or tap) for what the effect is and how long it has left.
      return h(
        'span',
        { class: `st ${kind}${d?.passive ? ' passive' : ''}`, data: { st: s.id }, tabIndex: 0 },
        iconEl(statusIcon(s.id)),
        s.stacks > 1 ? h('b', {}, String(s.stacks)) : null,
        h('em', { class: 'st-t' }, left(s)),
        h(
          'span',
          { class: 'st-tip' },
          h('strong', {}, `${d?.name ?? x?.name ?? s.id}${s.stacks > 1 ? ` ×${s.stacks}` : ''}`),
          h('span', {}, desc),
          h(
            'small',
            {},
            d?.passive
              ? 'ติดตัวถาวร'
              : !d
                ? kind === 'debuff'
                  ? 'ดีบัพ'
                  : 'สภาวะ'
                : d.kind === 'buff'
                  ? 'บัพ · หมดเวลาตามตัวเลขใต้ไอคอน'
                  : 'ดีบัพ · หมดเวลาตามตัวเลขใต้ไอคอน',
          ),
        ),
      );
    }),
  );
}

/** Stunned, as a status-row entry (for any fighter). */
const stunState = (e: Entity): RowState[] =>
  e.stunT > 0 ? [{ id: 'stun', stacks: 1, t: e.stunT, desc: 'ขยับและใช้ท่าไม่ได้ชั่วครู่' }] : [];

/** The line under the target's blood: the stance's seal glyph, what to do, and the key for it. */
const STANCE_HINT: Record<'block' | 'break' | 'attack' | 'stun', readonly [string, string, string]> = {
  block: ['守', `ศัตรูตั้งรับ · ใช้${BREAK}`, ''],
  break: ['破', `ศัตรูชาร์จ${BREAK} · โจมตีเพื่อขัดจังหวะ`, 'R'],
  attack: ['攻', 'ศัตรูกำลังโจมตี · คลิกขวาค้างเพื่อตั้งรับ', 'F'],
  stun: ['', 'ศัตรูมึนงง · ระดมโจมตี', ''],
};

/**
 * A HUD button's action: on pointer down (no click delay on touch), and on Enter / Space for
 * keyboard users, who send a click with no pointer down before it.
 */
function onPress(b: HTMLElement, fn: () => void): void {
  b.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    e.preventDefault();
    fn();
  });
  b.addEventListener('click', (e) => {
    if (e.detail === 0) fn();
  });
}

/** Turns a class on or off only when it changes. */
function flag(el: HTMLElement, cls: string, on: boolean): void {
  if (el.classList.contains(cls) !== on) el.classList.toggle(cls, on);
}

/** All heads-up display elements. Reads world state every frame; reacts to events for transient messages. */
export class Hud {
  /** Against a server only the zone's real chat shows: no made-up lines (net/switch.ts sets `window.__net`). */
  private get online(): boolean {
    return !!(window as unknown as { __net?: unknown }).__net;
  }
  private chatT = 6;
  /** Seconds of play with control (no scene): the made-up world chat waits for the first minute. */
  private calmT = 0;
  /** Seconds since the last training-cost line: they come at most every half minute. */
  private costT = 99;
  private toastT = 0;
  private bannerT = 0;
  private readonly slots: HTMLButtonElement[] = [];
  private sprintOn = false;
  private sprintSlot: HTMLButtonElement | null = null;
  private mountSlot: HTMLButtonElement | null = null;
  private renderedArt = '';
  private readonly miniCtx: CanvasRenderingContext2D;
  /** The training spot underfoot, in the player's frame (trainingSpot.ts). */
  private readonly spotBadge: SpotBadge;
  /** The stance hint last shown under the target (rebuilt only when it changes). */
  private hintKey = '';

  constructor(
    private readonly world: World,
    private readonly sprites: SpriteBank,
    private readonly minimapBase: HTMLCanvasElement,
    private readonly act: HudActions,
  ) {
    drawPortrait($<HTMLCanvasElement>('#portrait'), sprites.base.hero!);
    drawPortrait($<HTMLCanvasElement>('#npcFace'), sprites.base.elder!);
    const mini = $<HTMLCanvasElement>('#minimap');
    mini.width = Hud.MINI_TILES * 2;
    mini.height = Hud.MINI_TILES * 2;
    this.miniCtx = ctx2d(mini);
    this.buildHotbar();
    $('#dlgClose').addEventListener('click', () => this.closeDialog());
    $('#dialog').addEventListener('pointerdown', (e) => e.stopPropagation());
    this.renderQuest();
    // Money and points in the player's frame, each with its 16 px icon.
    for (const [id, cur] of [
      ['#cult', 'cultivation'],
      ['#gold', 'silver'],
      ['#jade', 'jade'],
    ] as const)
      $(id).parentElement?.prepend(iconEl(currencyIcon(cur, 16), 'ico16'));
    // Popovers and prompts keep clear of the HUD's blocks however tall they are (hud-glass.css).
    const status = $('#status');
    const right = $('#rightcol');
    const dock = $('#dock');
    const target = $('#target');
    const root = document.documentElement.style;
    const ro = new ResizeObserver(() => {
      root.setProperty('--status-h', `${status.offsetHeight}px`);
      root.setProperty('--map-h', `${right.offsetHeight}px`);
      root.setProperty('--hotbar-h', `${dock.offsetHeight}px`);
      root.setProperty('--hotbar-w', `${dock.offsetWidth}px`);
      root.setProperty('--target-h', `${target.offsetHeight}px`);
    });
    for (const el of [status, right, dock, target]) ro.observe(el);
    this.buildFolds();
    $('#pleave').addEventListener('click', () => this.act.command({ t: 'partyLeave' }));
    $('#tparty').addEventListener('click', () => {
      const t = this.world.get(this.world.player.targetId);
      const f = t && this.world.fellowOf(t);
      if (!t || !f) return;
      this.act.command(
        this.world.inParty(t) ? { t: 'partyKick', fellow: f.id } : { t: 'partyInvite', id: t.id },
      );
    });
    this.buildParty();
    this.spotBadge = new SpotBadge(world);
    if (!this.online) {
      this.chat('sys', 'เซิร์ฟเวอร์ ภูผาเก้ากระบี่ · ผู้เล่นออนไลน์ 1,284 คน');
      this.chat('world', 'ดาบเดียวพิชิตฟ้า: ใครเพิ่งเข้ามาใหม่ ไปคุยกับผู้เฒ่าหลิวที่ศิลาก่อนนะ');
    }
  }

  // ───────── hotbar ─────────

  /**
   * One row: basic attack (R) and guard (F); the active art's moves (1–8) as their icons; then
   * the movement keys as small labelled caps (jump, dodge, meditate, the flying sword, and the
   * sprint once วิ่งพันลี้ is learned).
   */
  private buildHotbar(): void {
    const bar = $('#hotbar');
    bar.replaceChildren();
    this.slots.length = 0;
    const skills = this.world.activeSkills();
    const art = this.world.activeArt();
    const moves = skills.map((s, i) => {
      const lv = this.world.skillLevel(s.id);
      const mark = moveMark(s);
      const b = this.slot(
        String(i + 1),
        moveFace(s),
        `slot skill ${KIND_CLASS[s.kind]}${mark ? ` f-${mark[0]}` : ''}`,
        h(
          'span',
          { class: 'tip' },
          h('span', { class: 'tip-han', ariaHidden: 'true' }, s.glyph),
          iconEl(skillIcon(s), 'tip-ico'),
          h('b', {}, `${s.name} ขั้น ${lv}`),
          h(
            'i',
            { class: KIND_CLASS[s.kind] },
            `${mark?.[1] ? `${KIND_LABEL[s.kind]} · ${mark[1]}` : KIND_LABEL[s.kind]}${s.lockForm ? ' · ท่าล็อกเป้า' : ''}`,
          ),
          s.desc,
          h(
            'em',
            {},
            `${s.rage ? `ความโกรธ ${s.rage} · ` : s.qi ? `ลมปราณ ${s.qi} · ` : ''}คูลดาวน์ ${s.cooldown} วินาที${s.windup ? ` · ชาร์จ ${s.windup} วินาที` : ''}`,
          ),
          h('em', {}, `${art.name} · เปลี่ยนวิชาที่เมนูวิชายุทธ์ (K)`),
        ),
        s.rage ? `怒${s.rage}` : s.qi ? String(s.qi) : '',
        SKILL_KIND_LABELS[s.kind],
      );
      groundDrag(b, i);
      onPress(b, () => this.act.useSlot(i));
      this.slots.push(b);
      return b;
    });

    // Basic attack: keep hitting the current target, or pick the nearest enemy.
    const basic = this.slot(
      'R',
      iconEl(actionIcon('attack')),
      'slot k-attack',
      h(
        'span',
        { class: 'tip' },
        h('b', {}, 'โจมตีธรรมดา'),
        'เข้าโจมตีเป้าหมายต่อเนื่อง (ไม่มีเป้าหมายจะเลือกศัตรูที่ใกล้ที่สุด) หรือคลิกที่ศัตรู แรงและระยะขึ้นกับวิชาที่ใช้อยู่ ไม่ใช้ลมปราณ',
        h('em', {}, `${SKILL_KIND_LABELS.attack} · ขัดจังหวะ${BREAK}ที่ศัตรูกำลังชาร์จได้`),
      ),
      '',
    );
    onPress(basic, () => this.basicAttack());
    const block = this.slot(
      'F',
      iconEl(actionIcon('guard')),
      'slot k-guard',
      h(
        'span',
        { class: 'tip' },
        h('b', {}, 'ตั้งรับ'),
        `กดค้าง (คลิกขวา หรือ F) ลดความเสียหายจากการโจมตีเหลือ 10% และทำให้ผู้โจมตีเสียจังหวะ แต่จะแตกเมื่อโดน${BREAK}`,
        h('em', {}, 'ใช้พลังกายต่อการรับ'),
      ),
      '',
    );
    const release = () => this.act.setBlock(false);
    block.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      e.preventDefault();
      block.setPointerCapture(e.pointerId);
      this.act.setBlock(true);
    });
    block.addEventListener('pointerup', release);
    block.addEventListener('pointercancel', release);
    // Enter / Space on the guard: a short guard (keyboard users hold F instead).
    block.addEventListener('click', (e) => {
      if (e.detail !== 0) return;
      this.act.setBlock(true);
      setTimeout(release, 400);
    });

    // Movement: the plain jump and dodge step everyone has, วิ่งพันลี้ only once learned.
    const qg = this.world.qgStats();
    const tip = (t: ActionTip) =>
      h('span', { class: 'tip' }, h('b', {}, t.title), t.body, h('em', {}, t.cost));
    const cap = (label: string, key: string, t: HTMLElement, cls = '') => {
      const b = h(
        'button',
        { type: 'button', class: `cap${cls}` },
        label ? h('span', {}, label) : null,
        h('kbd', {}, key),
        t,
      );
      b.setAttribute('aria-label', `${t.querySelector('b')?.textContent ?? label} (${key})`);
      return b;
    };
    const jump = cap('', 'Space', tip(jumpTip(qg)));
    jump.prepend(h('span', { class: 'cap-touch' }, 'กระโดด'));
    onPress(jump, () => this.act.command({ t: 'jump' }));
    const dash = cap('หลบ', 'Q', tip(dashTip(qg, !!this.world.progress.qinggong['swallow-dash'])));
    onPress(dash, () => this.act.dash());
    const sit = cap(
      'สมาธิ',
      'V',
      h(
        'span',
        { class: 'tip' },
        h('b', {}, 'นั่งสมาธิ'),
        'ฝึกปรือเร็วขึ้นห้าเท่า ฟื้นพลังชีวิตและลมปราณเร็วขึ้นสามเท่า ใช้ได้เมื่อไม่อยู่ในการต่อสู้',
        h('em', {}, 'ขยับตัวเพื่อเลิก'),
      ),
    );
    onPress(sit, () => this.act.command({ t: 'meditate' }));
    const ride = cap(
      'เหาะ',
      'X',
      h(
        'span',
        { class: 'tip' },
        h('b', {}, 'ขี่กระบี่เหาะ'),
        'ยืนบนกระบี่ที่ลอยเหนือพื้น เคลื่อนที่เร็วขึ้น 80% ใช้ได้เมื่อไม่อยู่ในการต่อสู้',
        h('em', {}, 'ใช้ท่า โจมตี ตั้งรับ กระโดด หรือโดนโจมตี จะลงจากกระบี่'),
      ),
    );
    onPress(ride, () => this.act.command({ t: 'mount' }));
    this.mountSlot = ride;
    const run = sprintTip(qg);
    const sprint = run ? cap('วิ่ง', 'Shift', tip(run), ' wide') : null;
    if (sprint) {
      sprint.classList.toggle('on', this.sprintOn);
      onPress(sprint, () => {
        this.sprintOn = !this.sprintOn;
        sprint.classList.toggle('on', this.sprintOn);
        this.act.command({ t: 'sprint', on: this.sprintOn });
      });
    }
    this.sprintSlot = sprint;

    bar.append(
      h('div', { class: 'hb-group hb-core' }, basic, block),
      h('i', { class: 'hb-sep' }),
      h('div', { class: 'hb-group hb-moves' }, ...moves),
      h('i', { class: 'hb-sep' }),
      h('div', { class: `hb-keys${sprint ? ' five' : ''}` }, jump, dash, sit, ride, sprint),
    );
    this.renderedArt = hotbarKey(this.world.progress);
  }

  toggleAuto(): void {
    this.act.command({ t: 'auto', on: !this.world.auto.on });
  }

  /** Engage the current target or the nearest enemy, or with none about, cut the air. */
  basicAttack(): void {
    const w = this.world;
    const t = w.get(w.player.targetId);
    if (t && !t.dead && t.kind === 'monster') this.act.command({ t: 'engage', id: t.id });
    else this.act.command({ t: 'attack' });
  }

  /** A hotbar key: its face, the key badge, the cost, the cooldown sweep and number, and the tooltip. */
  private slot(
    key: string,
    icon: HTMLElement | string,
    cls: string,
    tip: HTMLElement,
    cost: string,
    kind = '',
  ): HTMLButtonElement {
    const b = h(
      'button',
      { class: cls, type: 'button' },
      h('span', { class: 'glyph' }, icon),
      h('kbd', { class: 'key' }, key),
      cost ? h('span', { class: 'cost' }, cost) : null,
      h('span', { class: 'cd' }),
      h('span', { class: 'cdt' }),
      tip,
    );
    const name = tip.querySelector('b')?.textContent ?? '';
    b.setAttribute('aria-label', `${name} (${key})${kind ? ` ${kind}` : ''}`);
    return b;
  }

  // ───────── per-frame sync ─────────

  sync(dt: number): void {
    const w = this.world;
    const p = w.player;
    const pr = w.progress;
    if (hotbarKey(pr) !== this.renderedArt) this.buildHotbar();

    setWidth($('#hpbar i'), p.hp / p.maxHp);
    setText($('#hpbar b'), `${Math.ceil(p.hp)}/${p.maxHp}`);
    setWidth($('#qibar i'), p.qi / p.maxQi);
    setText($('#qibar b'), `${Math.floor(p.qi)}/${p.maxQi}`);
    setWidth($('#stbar i'), p.stamina / p.maxStamina);
    setText($('#stbar b'), `${Math.floor(p.stamina)}`);
    setWidth($('#ragebar i'), p.maxRage ? p.rage / p.maxRage : 0);
    setText($('#ragebar b'), `${Math.floor(p.rage)}/${p.maxRage}`);
    this.press($('#autoBtn'), w.auto.on);
    this.press($('#pkBtn'), p.pk);
    // A ladder bout: which ladder, against whom, and the time (queue or fight).
    const duel = w.duel;
    const duelBadge = $('#duelBadge');
    duelBadge.hidden = !duel;
    if (duel) {
      const name = duel.ladder ? LADDERS[duel.ladder].name : 'ประลองยุทธ';
      setText(
        duelBadge,
        duel.phase === 'queue'
          ? `${name} · กำลังจับคู่…`
          : duel.phase === 'countdown'
            ? `${name} · ${duel.foe?.name ?? ''} · เริ่มใน ${Math.ceil(duel.t)}`
            : duel.phase === 'fight'
              ? `${name} · ${duel.foe?.name ?? ''} · ${fmtClock(duel.t)}`
              : `${name} · ${duel.won ? 'ชนะ' : 'แพ้'}`,
      );
    }
    const jail = $('#jailBadge');
    jail.hidden = p.jailT <= 0;
    if (p.jailT > 0) {
      const m = Math.floor(p.jailT / 60);
      const s = Math.floor(p.jailT % 60);
      setText(jail, `牢 ติดคุก · เหลือ ${m}:${String(s).padStart(2, '0')}`);
    }
    if (this.mountSlot) {
      flag(this.mountSlot, 'on', !!p.mount);
      this.mountSlot.ariaPressed = String(!!p.mount);
    }
    if (this.sprintSlot) this.sprintSlot.ariaPressed = String(this.sprintOn);
    syncStatusRow($('#pstatus'), [...p.statuses, ...this.playerStates()]);
    setText($('#pname'), pr.name);
    setText($('#ptitle'), p.title);
    this.syncPortrait();
    const rank = rankFor(pr.cultivationSpent);
    setText($('#rank'), `${rank.realm} ${rank.stage}`);
    $('#rank').title = `ระดับพลังฝีมือ: ${rank.name}`;
    setText($('#cult'), Math.floor(pr.cultivation).toLocaleString('th-TH'));
    setText($('#gold'), pr.gold.toLocaleString('th-TH'));
    setText($('#jade'), pr.jade.toLocaleString('th-TH'));
    setText($('#coords'), `${Math.floor(p.x)}, ${Math.floor(p.y)}`);
    const hr = this.world.hour;
    const mm = Math.floor((hr % 1) * 60);
    setText(
      $('#clock'),
      `${hourName(hr)} ${String(Math.floor(hr)).padStart(2, '0')}:${String(mm - (mm % 10)).padStart(2, '0')}`,
    );
    flag($('#status'), 'meditating', p.meditating);
    this.spotBadge.sync();
    // What is training and the time left.
    const tr = pr.training;
    $('#training').hidden = !tr;
    if (tr) {
      setText($('#trName'), `${w.trainingName(tr)} ขั้น ${tr.level}`);
      setText($('#trLeft'), fmtClock(tr.left));
      setWidth($('#trBar i'), 1 - tr.left / tr.total);
      this.press($('#trAuto'), pr.trainAuto);
    }

    const skills = w.activeSkills();
    skills.forEach((s, i) => {
      const b = this.slots[i];
      if (!b) return;
      const cd = p.cooldowns[s.id] ?? 0;
      const deg = `${((cd / s.cooldown) * 360).toFixed(0)}deg`;
      const k = b as HTMLButtonElement & { _d?: string; _l?: boolean; _c?: boolean };
      if (k._d !== deg) {
        k._d = deg;
        b.style.setProperty('--p', deg);
      }
      setText(b.querySelector<HTMLElement>('.cdt')!, cd > 0 ? String(Math.ceil(cd)) : '');
      if (k._c !== cd > 0) {
        k._c = cd > 0;
        b.classList.toggle('cooling', cd > 0);
      }
      const low = p.qi < s.qi || (!!s.rage && p.rage < s.rage);
      if (k._l !== low) {
        k._l = low;
        b.classList.toggle('low', low);
      }
    });
    syncUltReady(this.slots, skills, p);
    // The move being charged or channelled lights its key.
    const casting = p.cast?.skillId;
    skills.forEach((s, i) => this.slots[i] && flag(this.slots[i], 'casting', casting === s.id));

    this.syncTarget();
    this.syncParty();
    this.syncMinimap();

    // The nearest living boss (the quest boss or the world boss) gets the big health bar.
    const boss = [w.boss, w.worldBoss].find((b) => b && !b.dead && Math.hypot(b.x - p.x, b.y - p.y) < 14);
    const showBoss = !!boss;
    $('#bossbar').hidden = !showBoss;
    if (showBoss && boss) {
      setText($('#bossbar .bn'), boss.name);
      setWidth($('#bossbar i'), boss.hp / boss.maxHp);
      setText($('#bossbar span'), `${Math.ceil(boss.hp)} / ${boss.maxHp}`);
    }

    $('#death').hidden = !p.dead;
    if (p.dead) setText($('#deathT'), `จะฟื้นที่ลานศิลาในอีก ${Math.max(1, Math.ceil(p.deadT))} วินาที`);

    this.calmT = this.world.scene || p.dead ? 0 : this.calmT + dt;
    this.costT += dt;
    this.chatT -= dt;
    if (this.chatT <= 0 && this.calmT > 60 && !this.online) {
      this.chatT = 9 + Math.random() * 9;
      // Made-up chat of a sect only once the player is in one; the world chat is for everyone.
      const pool = AMBIENT_CHAT.filter(([c]) => c !== 'guild' || !!this.world.progress.school);
      const [ch, text] = pool[Math.floor(Math.random() * pool.length)]!;
      this.chat(ch, text);
    }
    if (this.toastT > 0 && (this.toastT -= dt) <= 0) $('#toast').classList.remove('show');
    if (this.bannerT > 0 && (this.bannerT -= dt) <= 0) $('#banner').classList.remove('show');
  }

  /** A toggle button's state: `aria-pressed` and the `on` class, written only when it changes. */
  private press(el: HTMLElement, on: boolean): void {
    if (el.ariaPressed !== String(on)) el.ariaPressed = String(on);
    flag(el, 'on', on);
  }

  /** The player's states shown as status icons: stunned, meditating, a training spot, the dance's afterglow. */
  private playerStates(): RowState[] {
    const w = this.world;
    const p = w.player;
    const out = stunState(p);
    if (p.meditating)
      out.push({
        id: 'meditation',
        stacks: 1,
        t: Infinity,
        desc: 'ฝึกปรือเร็วขึ้นห้าเท่า ฟื้นพลังชีวิตและลมปราณเร็วขึ้นสามเท่า · ขยับตัวเพื่อเลิก',
      });
    const spot = w.spotState();
    if (spot && !spot.paused)
      out.push({
        id: 'training-spot',
        stacks: 1,
        t: Infinity,
        desc: `${spot.spot.name} · ฝึกปรือ ${fmtMult(spot.mult)}`,
      });
    const dance = w.progress.groupDance;
    if (dance.buffLeft > 0)
      out.push({
        id: 'group-dance',
        stacks: 1,
        t: dance.buffLeft,
        desc: `ฝึกปรือ ×${dance.buffMult} จากการรำกลุ่ม`,
      });
    return out;
  }

  /** Party frames: one per member, clicked to pick them as the target. */
  private partyFrames: {
    e: Entity;
    root: HTMLElement;
    hp: HTMLElement;
    hpText: HTMLElement;
    qi: HTMLElement;
    note: HTMLElement;
  }[] = [];

  private buildParty(): void {
    const w = this.world;
    const members = w.partyMembers();
    $('#party').hidden = members.length === 0;
    setText($('#pcount'), `${members.length + 1}/${PARTY.maxSize}`);
    this.partyFrames = members.map((e) => {
      const hp = h('div', { class: 'g-track hp' }, h('i'));
      const qi = h('div', { class: 'g-track thin qi' }, h('i'));
      const hpText = h('b', { class: 'g-num' });
      const note = h('em');
      const root = h(
        'button',
        { type: 'button', class: 'pm', title: 'คลิกเพื่อเลือกเป็นเป้าหมาย' },
        h('span', { class: 'pn' }, h('span', {}, e.name), note, hpText),
        hp,
        qi,
      );
      root.addEventListener('click', () => this.act.command({ t: 'select', id: e.id }));
      return { e, root, hp, hpText, qi, note };
    });
    $('#pmembers').replaceChildren(...this.partyFrames.map((f) => f.root));
  }

  private syncParty(): void {
    const sel = this.world.player.targetId;
    for (const f of this.partyFrames) {
      const { e } = f;
      setWidth(f.hp.querySelector('i')!, e.dead ? 0 : e.hp / e.maxHp);
      setText(f.hpText, e.dead ? 'หมดสติ' : `${Math.ceil(e.hp)}/${e.maxHp}`);
      setWidth(f.qi.querySelector('i')!, e.qi / e.maxQi);
      setText(
        f.note,
        e.dead
          ? `ฟื้นใน 0:${String(Math.max(1, Math.ceil(PARTY.reviveSeconds - e.deadT))).padStart(2, '0')}`
          : e.combatT > 0
            ? 'กำลังสู้'
            : '',
      );
      flag(f.root, 'down', e.dead);
      flag(f.root, 'sel', sel === e.id);
    }
  }

  /**
   * The player's portrait: drawn once their dressed figure is ready and again whenever it changes
   * (the figure built at start-up is a blank stand-in until the rig has drawn it).
   */
  private syncPortrait(force = false): void {
    const set = this.sprites.of(this.world.player) ?? this.sprites.base.hero;
    const img = set?.idle[0];
    if (!set || !img || (!force && img === this.playerFace)) return;
    this.playerFace = img;
    drawPortrait($<HTMLCanvasElement>('#portrait'), set);
  }

  /**
   * Folds and the menu: the chat folds away (folded by default on narrow screens), the quest's
   * objectives fold under its title (folded by default on a phone), and เมนู (Esc) opens the menu
   * that holds every window's button.
   */
  private buildFolds(): void {
    const hud = $('#hud');
    const stop = (e: Event) => e.stopPropagation();
    const chat = $('#chat');
    chat.setAttribute('role', 'log');
    chat.setAttribute('aria-label', 'แชท');
    const chatBtn = h('button', { type: 'button', id: 'chatToggle' }, 'แชท');
    chatBtn.setAttribute('aria-controls', 'chat');
    const setChat = (open: boolean) => {
      hud.dataset.chat = open ? 'open' : 'closed';
      chatBtn.setAttribute('aria-expanded', String(open));
      chatBtn.title = open ? 'ซ่อนแชท' : 'แสดงแชท';
    };
    setChat(!matchMedia('(max-width: 960px)').matches);
    chatBtn.addEventListener('pointerdown', stop);
    chatBtn.addEventListener('click', () => setChat(hud.dataset.chat !== 'open'));
    chat.before(chatBtn);

    const quest = $('#quest');
    const head = $('#quest h3');
    head.tabIndex = 0;
    head.setAttribute('role', 'button');
    const setQuest = (open: boolean) => {
      quest.classList.toggle('folded', !open);
      head.setAttribute('aria-expanded', String(open));
      head.title = open ? 'ย่อภารกิจ' : 'ดูเป้าหมายภารกิจ';
    };
    setQuest(!matchMedia('(max-width: 760px)').matches);
    const flipQuest = () => setQuest(quest.classList.contains('folded'));
    head.addEventListener('click', flipQuest);
    head.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      e.stopPropagation();
      flipQuest();
    });

    const menu = $('#gameMenu');
    const menuBtn = $('#menuBtn');
    menuBtn.addEventListener('pointerdown', stop);
    menuBtn.addEventListener('click', () => this.toggleMenu());
    menu.addEventListener('pointerdown', stop);
    // Picking anything in the menu closes it; so does a press anywhere else.
    menu.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('button')) this.closeMenu();
    });
    addEventListener(
      'pointerdown',
      (e) => {
        const t = e.target as Node;
        if (this.menuOpen && !menu.contains(t) && !menuBtn.contains(t)) this.closeMenu();
      },
      true,
    );
  }

  get menuOpen(): boolean {
    return !$('#gameMenu').hidden;
  }

  toggleMenu(): void {
    if (this.menuOpen) this.closeMenu();
    else {
      $('#gameMenu').hidden = false;
      $('#menuBtn').setAttribute('aria-expanded', 'true');
      $('#hud').classList.add('menu-open');
      $<HTMLButtonElement>('#gameMenu button').focus({ preventScroll: true });
    }
  }

  closeMenu(): void {
    if (!this.menuOpen) return;
    $('#gameMenu').hidden = true;
    $('#menuBtn').setAttribute('aria-expanded', 'false');
    $('#hud').classList.remove('menu-open');
  }

  private syncTarget(): void {
    const w = this.world;
    const t = w.get(w.player.targetId);
    const frame = $('#target');
    if (!t) {
      frame.hidden = true;
      return;
    }
    frame.hidden = false;
    this.syncTargetHead(t);
    setWidth($('#tbar i'), t.dead ? 0 : t.hp / t.maxHp);
    // Fellow wanderers can be asked into the party (or sent away from it).
    const f = w.fellowOf(t);
    const pbtn = $('#tparty');
    const joined = w.inParty(t);
    const full = w.progress.party.length + 1 >= PARTY.maxSize;
    pbtn.hidden = !f || (!joined && full);
    if (f) setText(pbtn, joined ? 'ส่งออกจากปาร์ตี้' : 'ชวนเข้าปาร์ตี้');
    syncStatusRow($('#tstatus'), [...t.statuses, ...stunState(t)]);
    setText($('#thp'), t.dead ? 'หมดสติ' : `${Math.ceil(t.hp)}/${t.maxHp}`);
    const stance =
      t.stunT > 0
        ? 'stun'
        : t.blocking
          ? 'block'
          : t.cast?.kind === 'break'
            ? 'break'
            : t.cast
              ? 'attack'
              : null;
    const el = $('#thint');
    if (this.hintKey !== (stance ?? '')) {
      this.hintKey = stance ?? '';
      el.className = `thint${stance ? ` h-${stance}` : ''}`;
      if (!stance) el.replaceChildren();
      else {
        const [glyph, text, key] = STANCE_HINT[stance];
        el.replaceChildren(
          glyph ? h('span', { class: 'han' }, glyph) : iconEl(statusIcon('stun'), 'icon st-ico'),
          text,
          key ? h('kbd', {}, key) : '',
        );
      }
    }
  }

  /** The player's portrait as last drawn (its standing frame), redrawn when the look or the frame changes. */
  private playerFace: HTMLCanvasElement | undefined;

  /**
   * The target frame's head: kind (NPC, friendly, hostile, boss), name, level, title, qi and rage
   * for fighters, and the dead look.
   */
  private targetFace = -1;

  private syncTargetHead(t: Entity): void {
    const w = this.world;
    const p = w.player;
    const frame = $('#target');
    const boss = t.kind === 'monster' && (t.brain?.defId === 'boss' || t.brain?.defId === 'worldboss');
    const person = t.kind === 'npc' || t.kind === 'ambient';
    const foe = !person && (t.kind === 'monster' || w.isHostile(p, t));
    const kind = boss ? 'boss' : foe ? 'foe' : person ? 'npc' : 'friend';
    const cls = `g-panel k-${kind}${t.dead ? ' dead' : ''}`;
    if (frame.className !== cls) frame.className = cls;
    setText(
      $('#tkind'),
      boss ? 'บอส' : foe ? 'ศัตรู' : t.kind === 'npc' ? 'NPC' : t.kind === 'ambient' ? 'ชาวบ้าน' : 'มิตร',
    );
    setText($('#tname'), t.name);
    $('#tname').style.color = t.kind === 'monster' ? threatColor(monsterRank(t.level), rankFor(w.progress.cultivationSpent).index) : '';
    // The face in the frame (the context menu opens on it): redrawn when the target changes.
    if (this.targetFace !== t.id) {
      const face = this.sprites.of(t);
      if (face) {
        drawPortrait($<HTMLCanvasElement>('#tface'), face);
        this.targetFace = t.id;
      }
    }
    const fighter = t.kind === 'monster' || t.kind === 'rival' || t.kind === 'player';
    setText($('#tlv'), fighter ? `ระดับ ${t.level}` : '');
    setText(
      $('#ttitle'),
      t.kind === 'monster'
        ? rankName(monsterRank(t.level))
        : t.kind === 'rival'
          ? [w.inParty(t) ? 'เพื่อนร่วมปาร์ตี้' : KARMA_LABELS[t.karma], t.pk ? 'เปิดแดง' : '', t.title]
              .filter(Boolean)
              .join(' · ')
          : lifeTitle(w, t),
    );
    const qi = $('#tqi');
    qi.hidden = !fighter || t.maxQi <= 0;
    if (!qi.hidden) setWidth(qi.querySelector('i')!, t.qi / t.maxQi);
    const rage = $('#trage');
    rage.hidden = !fighter || t.maxRage <= 0;
    if (!rage.hidden) setWidth(rage.querySelector('i')!, t.rage / t.maxRage);
  }

  /** Turns the minimap with the camera so it always matches the view. */
  setMapRotation(quarterTurns: number): void {
    $('#minimap').style.setProperty('--rot', String(quarterTurns));
  }

  /** The minimap shows this many tiles across, centred on the player. */
  static readonly MINI_TILES = 72;

  private syncMinimap(): void {
    const g = this.miniCtx;
    const w = this.world;
    const p = w.player;
    const S = g.canvas.width;
    // Map pixels (2 per tile) to minimap pixels, the player in the middle.
    const ox = S / 2 - p.x * 2;
    const oy = S / 2 - p.y * 2;
    const at = (x: number, y: number) => [Math.floor(x * 2 + ox), Math.floor(y * 2 + oy)] as const;
    g.fillStyle = '#15111a';
    g.fillRect(0, 0, S, S);
    g.drawImage(this.minimapBase, Math.round(ox), Math.round(oy));
    drawSpotMarks(g, w, at, 2);
    drawLifePings(g, w, at);
    drawQuestPing(g, w, at);
    // Warp circles: a blue ring at the middle of each edge with a neighbour beyond.
    g.fillStyle = '#5aaaff';
    for (const wp of w.map.portals) {
      const [x, y] = at(wp.x, wp.y);
      g.fillRect(x - 2, y - 2, 5, 5);
      g.clearRect(x - 1, y - 1, 3, 3);
      g.fillRect(x, y, 1, 1);
    }
    for (const e of w.entities.values()) {
      if (e.dead) continue;
      const [x, y] = at(e.x, e.y);
      if (x < -4 || y < -4 || x > S + 4 || y > S + 4) continue;
      if (e.kind === 'monster') {
        const boss = e.brain?.defId === 'boss' || e.brain?.defId === 'worldboss';
        g.fillStyle = boss ? '#c05bff' : '#ff5a4a';
        g.fillRect(x - (boss ? 1 : 0), y - (boss ? 1 : 0), boss ? 4 : 2, boss ? 4 : 2);
      } else if (e.kind === 'ambient' || e.kind === 'rival') {
        g.fillStyle = w.inParty(e) ? '#8ff0a8' : '#7fb6ff';
        g.fillRect(x, y, 2, 2);
      } else if (e.kind === 'npc') {
        g.fillStyle = '#74d4ad';
        g.fillRect(x, y, 2, 2);
      }
    }
    g.fillStyle = '#fff3c4';
    g.fillRect(S / 2 - 1, S / 2 - 1, 3, 3);
  }

  // ───────── events ─────────

  handle(ev: WorldEvent): void {
    switch (ev.type) {
      case 'party':
        this.buildParty();
        break;
      case 'notice':
        this.toast(ev.text);
        break;
      case 'log':
        this.chat(ev.channel, ev.text);
        break;
      case 'dialog':
        this.openDialog(ev.npcId, ev.text, ev.action, ev.schoolId, ev.label);
        break;
      case 'quest':
        this.renderQuest();
        if (ev.status === 'completed') this.banner('ภารกิจสำเร็จ');
        break;
      case 'bossSpawn':
        this.toast('ปีศาจกระบี่ทมิฬปรากฏตัวทางทิศใต้');
        break;
      case 'bossDefeated':
        this.banner('ปราบปีศาจกระบี่ทมิฬ');
        break;
      case 'trained':
        this.toast(
          ev.what === 'skill' ? `ขัดเกลากระบวนท่าถึงขั้น ${ev.level}` : `กำลังภายในทะลวงถึงขั้น ${ev.level}`,
        );
        if (ev.what === 'neigong' && ev.level >= getNeigong(ev.id).maxLevel)
          this.banner(`ฝึกจนตัน ได้รับออร่า「${getNeigong(ev.id).aura.name}」`);
        break;
      case 'meditate':
        if (ev.on) this.toast('นั่งสมาธิ · ขยับตัวเพื่อเลิก');
        break;
      case 'trainingSpot': {
        const msg = spotMessage(ev, this.world);
        if (msg?.banner) this.banner(msg.banner);
        if (msg?.toast) this.toast(msg.toast);
        break;
      }
      case 'pkColor':
        if (ev.id === this.world.player.id)
          this.toast(
            ev.color === 'purple'
              ? 'หัวม่วง! ค่า PK เกิน 5000'
              : ev.color === 'red'
                ? 'หัวแดง! ทหาร NPC จะโจมตี ตายแล้วติดคุก'
                : ev.color === 'orange'
                  ? 'หัวส้ม · ได้รับค่า PK'
                  : 'ชื่อกลับเป็นปกติ',
          );
        break;
      case 'jail':
        this.banner(ev.seconds > 0 ? `ติดคุก ${Math.round(ev.seconds / 60)} นาที` : 'พ้นโทษ ออกจากคุก');
        break;
      case 'karma':
        if (ev.id === this.world.player.id) this.banner(`กลายเป็นฝ่าย「${KARMA_LABELS[ev.karma]}」`);
        break;
      case 'title':
        this.banner(`ได้รับฉายา「${getTitle(ev.id).name}」`);
        break;
      case 'mount':
        if (ev.mount)
          this.toast(
            getMount(ev.mount).fly
              ? `ขี่${getMount(ev.mount).name} · กด Space ค้างบินขึ้น · กด Q ค้างบินลง · X ลงจากหลัง`
              : `ขี่${getMount(ev.mount).name} · กด X เพื่อลง`,
          );
        break;
      case 'joinedSchool':
        this.banner(`เข้าสำนัก${getSchool(ev.school).name}`);
        break;
      case 'loot':
        this.toast(
          `ได้รับ「${itemOf(this.world.progress.rolls, ev.item)?.name ?? ''}」· เปิดกระเป๋า (I) เพื่อสวมใส่`,
        );
        break;
      case 'learnedArt':
        this.toast(`เรียนสำเร็จ: ${getArt(ev.art).name} · เปิดแผงวิชา (K) เพื่อสลับมาใช้`);
        break;
      case 'qinggong':
        this.toast(
          ev.level === 1
            ? `เรียนวิชาตัวเบาสำเร็จ: ${getQinggong(ev.id).name}`
            : `${getQinggong(ev.id).name} ขั้น ${ev.level}`,
        );
        break;
      case 'progressChanged':
        this.syncPortrait(true);
        this.renderQuest();
        break;
      case 'ladder':
        if (ev.phase === 'countdown')
          this.banner(`${ev.ladder ? LADDERS[ev.ladder].name : 'ประลองยุทธ'} · พบคู่ประลอง ${ev.foe}`);
        else if (ev.phase === 'fight') this.banner(ev.ladder ? 'เริ่มประลอง!' : 'เริ่มประลอง! สู้อัตโนมัติ');
        else if (ev.phase === 'result')
          this.banner(
            `${ev.won ? 'ชนะ' : 'แพ้'}${ev.delta !== undefined ? ` · คะแนน ${ev.delta >= 0 ? '+' : ''}${ev.delta}` : ''}`,
          );
        break;
      default:
        break;
    }
  }

  renderQuest(): void {
    const w = this.world;
    const q = w.currentQuest();
    const qs = w.progress.quest;
    const qt = $('#qt');
    const qp = $('#qp');
    qp.replaceChildren();
    // Origins B/C/D run their own chain (originFullOf): its step and counts fill the strip.
    const og = originFullOf(w)?.quest;
    if (og) {
      qt.textContent = og.quest.title;
      og.quest.objectives.forEach((o, i) => {
        const n = Math.min(o.count, og.progress[i] ?? 0);
        qp.append(h('div', { class: n >= o.count ? 'ok' : '' }, `${o.label} ${n}/${o.count}`));
      });
      return;
    }
    // Origin A's plains chain is not shown to the others until they take it.
    const hidden = !!q && !showsPlainsQuest(originOf(w.progress), qs.status);
    const ch2 = ch2Goal(w.progress);
    if ((!q || hidden) && ch2) {
      qt.textContent = ch2.title;
      qp.append(h('div', {}, ch2.goal));
      return;
    }
    if (!q || hidden) {
      qt.textContent = 'ตำนานเริ่มต้น';
      qp.append(h('div', {}, 'ฝึกปรือและสำรวจยุทธภพได้อย่างอิสระ'));
      return;
    }
    qt.textContent = q.title;
    if (qs.status === 'available') qp.append(h('div', {}, questGiverLine(q, 'available')));
    else if (qs.status === 'ready') qp.append(h('div', { class: 'ok' }, questGiverLine(q, 'ready')));
    else
      q.objectives.forEach((o, i) => {
        const n = Math.min(o.count, qs.progress[i] ?? 0);
        qp.append(h('div', { class: n >= o.count ? 'ok' : '' }, `${o.label} ${n}/${o.count}`));
      });
  }

  chat(ch: ChatChannel, text: string): void {
    // The training cost line comes every few seconds: show it once in a while, in plain words.
    if (text.startsWith('ฝึก') && text.includes('สูญเสียค่าฝึกฝน')) {
      if (this.costT < 30) return;
      this.costT = 0;
      text = `${text.split(' สูญเสียค่าฝึกฝน')[0]} · ใช้แต้มฝึกปรือเป็นค่าฝึกทุก 5 วินาที`;
    }
    const box = $('#chat');
    const [cls, label] = CHANNELS[ch];
    box.append(h('div', { class: cls }, h('b', {}, label), ' ', text));
    // Only whole lines: the oldest go once the box is full (and never more than nine are kept).
    while (box.children.length > 9) box.firstElementChild?.remove();
    // The box keeps its newest line at the bottom, so a line cut off at the top starts above it.
    while (box.children.length > 1 && (box.firstElementChild as HTMLElement).offsetTop < 0)
      box.firstElementChild?.remove();
  }

  toast(msg: string): void {
    const t = $('#toast');
    t.textContent = msg;
    t.classList.add('show');
    this.toastT = 2.6;
  }

  banner(msg: string): void {
    const b = $('#banner');
    b.textContent = msg;
    b.classList.add('show');
    this.bannerT = 2.4;
  }

  private openDialog(
    npcId: number,
    text: string,
    action: 'accept' | 'turnIn' | 'joinSchool' | 'shop' | 'travel' | null,
    schoolId?: string,
    label?: string,
  ): void {
    const npc = this.world.get(npcId);
    if (npc) {
      $('#dlgName').textContent = npc.name;
      $('#dlgRole').textContent = npc.title;
      const face = this.sprites.of(npc);
      if (face) drawPortrait($<HTMLCanvasElement>('#npcFace'), face);
    }
    $('#dlgText').textContent = text;
    $('#dlgText').style.whiteSpace = 'pre-line';
    const ok = $<HTMLButtonElement>('#dlgOk');
    ok.hidden = !action;
    ok.textContent =
      label ??
      (action === 'accept'
        ? 'รับภารกิจ'
        : action === 'turnIn'
          ? 'ส่งภารกิจ'
          : action === 'shop'
            ? 'ดูสินค้า'
            : action === 'travel'
              ? 'ออกเดินทาง'
              : 'เข้าสำนัก');
    ok.onclick = () => {
      this.closeDialog();
      if (action === 'accept') this.act.command({ t: 'questAccept' });
      else if (action === 'turnIn') this.act.command({ t: 'questTurnIn' });
      else if (action === 'joinSchool' && schoolId) this.act.command({ t: 'joinSchool', id: schoolId });
      else if (action === 'shop') this.act.openPanel('shop');
      else if (action === 'travel') this.act.command({ t: 'travel' });
    };
    $('#dialog').hidden = false;
    (action ? ok : $('#dlgClose')).focus();
  }

  closeDialog(): void {
    $('#dialog').hidden = true;
  }

  get dialogOpen(): boolean {
    return !$('#dialog').hidden;
  }
}
